(() => {
  const DIRECTORY_SELECTOR = '.sw-device-directory';

  const initializeDirectory = (section) => {
    if (!section || section.dataset.initialized === 'true') return;
    const source = window.SlickwrapsDeviceSearch;
    if (!source) return;
    const input = section.querySelector('.sw-device-directory__input');
    const brandList = section.querySelector('.sw-device-directory__brands');
    const grid = section.querySelector('.sw-device-directory__grid');
    const status = section.querySelector('.sw-device-directory__status');
    if (!input || !brandList || !grid || !status) return;

    section.dataset.initialized = 'true';
    section._swDirectoryController?.abort();
    const controller = new AbortController();
    const { signal } = controller;
    section._swDirectoryController = controller;
    const clearButton = section.querySelector('.sw-device-directory__clear');
    const submitButton = section.querySelector('.sw-device-directory__submit');
    const resetButton = section.querySelector('.sw-device-directory__reset');
    const resultsHeading = section.querySelector('.sw-device-directory__results-heading');
    const defaultHeading = resultsHeading?.textContent.trim() || 'All Devices';
    const categoryButtons = Array.from(section.querySelectorAll('.sw-device-directory__category'));
    const emptyState = section.querySelector('.sw-device-directory__empty');
    const moreWrap = section.querySelector('.sw-device-directory__more-wrap');
    const moreButton = section.querySelector('.sw-device-directory__more');
    const initialMarkup = grid.innerHTML;
    let query = '';
    let selectedCategory = '';
    let selectedBrand = '';
    let requestId = 0;
    let inputTimer;
    let failed = false;

    const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[character]);
    const getReleaseYear = (device) => {
      const value = Number(device.releaseYear);
      return Number.isInteger(value) && value >= 1900 && value <= 2200 ? value : null;
    };
    const imageURLForWidth = (url, width) => {
      if (!url) return '';
      try {
        const image = new URL(url, window.location.href);
        image.searchParams.set('width', String(width));
        return image.toString();
      } catch (_) { return ''; }
    };

    const cardMarkup = (
      device,
      index
    ) => {
      const title = escapeHTML(
        device.title
      );

      const brand = escapeHTML(
        device.brand
      );

      const url = escapeHTML(
        device.url
      );

      const releaseYear =
        getReleaseYear(device);

      const image180 = escapeHTML(
        imageURLForWidth(
          device.image,
          180
        )
      );

      const image280 = escapeHTML(
        imageURLForWidth(
          device.image,
          280
        )
      );

      const image420 = escapeHTML(
        imageURLForWidth(
          device.image,
          420
        )
      );

      return `
        <a
          href="${url}"
          class="sw-device-directory__card"
          aria-label="Shop ${title} skins"
          itemprop="itemListElement"
          itemscope
          itemtype="https://schema.org/ListItem"
        >
          <meta
            itemprop="position"
            content="${index + 1}"
          >

          <meta
            itemprop="name"
            content="${title}"
          >

          <span
            class="sw-device-directory__card-image"
            itemprop="item"
          >
            ${
              image420
                ? `
                  <img
                    src="${image280 || image420}"
                    srcset="
                      ${image180} 180w,
                      ${image280} 280w,
                      ${image420} 420w
                    "
                    sizes="(max-width: 600px) 45vw, (max-width: 850px) 30vw, (max-width: 1100px) 23vw, 220px"
                    alt="${title} skins and wraps"
                    width="420"
                    height="340"
                    loading="lazy"
                    decoding="async"
                  >
                `
                : `
                  <span class="sw-device-directory__placeholder">
                    <svg
                      viewBox="0 0 32 32"
                      aria-hidden="true"
                    >
                      <rect
                        x="9"
                        y="3"
                        width="14"
                        height="26"
                        rx="3"
                      ></rect>
                      <path d="M14 25h4"></path>
                    </svg>
                  </span>
                `
            }
          </span>

          <span class="sw-device-directory__card-content">
            ${
              brand || releaseYear !== null
                ? `
                  <span class="sw-device-directory__card-meta">
                    ${
                      brand
                        ? `
                          <span class="sw-device-directory__card-brand">
                            ${brand}
                          </span>
                        `
                        : ''
                    }

                    ${
                      brand && releaseYear !== null
                        ? `
                          <span
                            class="sw-device-directory__card-separator"
                            aria-hidden="true"
                          >·</span>
                        `
                        : ''
                    }

                    ${
                      releaseYear !== null
                        ? `
                          <span class="sw-device-directory__card-year">
                            ${releaseYear}
                          </span>
                        `
                        : ''
                    }
                  </span>
                `
                : ''
            }

            <span class="sw-device-directory__card-title">
              ${title}
            </span>

            <span class="sw-device-directory__card-action">
              View Skins
              <span aria-hidden="true">→</span>
            </span>
          </span>
        </a>
      `;
    };


    const setCategoryActiveState = () => {
      categoryButtons.forEach((button) => {
        const active = (button.dataset.category || '') === selectedCategory;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const renderBrands = () => {
      if (!source.isComplete()) return;
      const brands = Array.from(new Set(source.search('', { category: selectedCategory })
        .map((device) => String(device.brand || '').trim()).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      // Do not clear a deliberately selected brand merely because a request is pending.
      brandList.innerHTML = ['', ...brands].map((brand) => {
        const active = brand === selectedBrand;
        return `<button type="button" class="sw-device-directory__brand ${active ? 'is-active' : ''}" data-brand="${escapeHTML(brand)}" aria-pressed="${active}">${escapeHTML(brand || 'All Brands')}</button>`;
      }).join('');
    };

    const updateHeading = () => {
      if (resultsHeading) resultsHeading.textContent = query ? 'Search Results' :
        selectedBrand || selectedCategory || defaultHeading;
      if (resetButton) resetButton.hidden = !(query || selectedCategory || selectedBrand);
      if (clearButton) clearButton.hidden = !input.value;
    };

    const render = () => {
      if (!source.isComplete()) return;
      failed = false;
      section.classList.add('is-enhanced');
      const matches = source.search(query, { category: selectedCategory, brand: selectedBrand, sort: 'published-desc' });
      grid.innerHTML = matches.map(cardMarkup).join('');
      grid.hidden = !matches.length;
      grid.setAttribute('aria-busy', 'false');
      if (emptyState) emptyState.hidden = matches.length !== 0;
      status.textContent = `${matches.length} ${matches.length === 1 ? 'device' : 'devices'}`;
      if (moreWrap) moreWrap.hidden = true;
      updateHeading();
    };

    const showFailure = () => {
      failed = true;
      section.classList.remove('is-enhanced');
      grid.innerHTML = initialMarkup;
      grid.hidden = false;
      grid.setAttribute('aria-busy', 'false');
      if (emptyState) emptyState.hidden = true;
      status.textContent = 'Device search could not finish loading. Please retry, or browse the device pages below.';
      if (moreWrap) moreWrap.hidden = false;
      if (moreButton) {
        moreButton.disabled = false;
        moreButton.textContent = 'Retry Device Search';
      }
    };

    const runSearch = async () => {
      window.clearTimeout(inputTimer);
      query = source.normalize(input.value.trim());
      const currentRequest = ++requestId;
      failed = false;
      updateHeading();
      if (!source.isComplete()) {
        // Only a failed/incomplete server scan needs the paginated fallback.
        status.textContent = query || selectedCategory || selectedBrand ? 'Searching all devices…' : 'Loading all devices…';
        grid.setAttribute('aria-busy', 'true');
        grid.hidden = true;
        if (emptyState) emptyState.hidden = true;
        if (moreWrap) moreWrap.hidden = true;
      }
      try {
        await source.load();
        if (signal.aborted || currentRequest !== requestId) return;
        renderBrands();
        render();
      } catch (_) {
        if (!signal.aborted && currentRequest === requestId) showFailure();
      }
    };

    const clearSearch = (allFilters = false) => {
      ++requestId;
      window.clearTimeout(inputTimer);
      input.value = '';
      query = '';
      if (allFilters) {
        selectedCategory = '';
        selectedBrand = '';
        setCategoryActiveState();
      }
      if (source.isComplete()) {
        renderBrands();
        render();
      } else {
        runSearch();
      }
    };

    input.addEventListener('focus', () => {
      // One shared promise, also used by the header and homepage Finder.
      source.load().then(() => {
        if (!signal.aborted) renderBrands();
      }).catch(() => { /* An active search has its own visible retry state. */ });
    }, { signal });
    input.addEventListener('input', () => {
      ++requestId; // Immediately invalidate the previous query, including during debounce.
      if (clearButton) clearButton.hidden = !input.value;
      window.clearTimeout(inputTimer);
      inputTimer = window.setTimeout(runSearch, 120);
    }, { signal });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') { event.preventDefault(); runSearch(); }
      if (event.key === 'Escape') { clearSearch(); input.blur(); }
    }, { signal });
    submitButton?.addEventListener('click', (event) => { event.preventDefault(); runSearch(); }, { signal });
    clearButton?.addEventListener('click', () => { clearSearch(); input.focus(); }, { signal });
    resetButton?.addEventListener('click', () => clearSearch(true), { signal });
    categoryButtons.forEach((button) => button.addEventListener('click', () => {
      selectedCategory = button.dataset.category || '';
      selectedBrand = '';
      setCategoryActiveState();
      runSearch();
    }, { signal }));
    brandList.addEventListener('click', (event) => {
      const button = event.target.closest('.sw-device-directory__brand');
      if (!button || !brandList.contains(button)) return;
      selectedBrand = button.dataset.brand || '';
      runSearch();
    }, { signal });
    moreButton?.addEventListener('click', () => {
      if (failed) runSearch();
    }, { signal });

    // Preserve the complete server-rendered grid: no initial shrink or reordering.
    // Other page types keep on-demand loading; this directory promises All Devices.
    setCategoryActiveState();
    const initialQuery = new URLSearchParams(window.location.search).get('q') || '';
    if (initialQuery) { input.value = initialQuery; runSearch(); }
    else if (section.dataset.directoryComplete === 'true' && source.isComplete()) {
      renderBrands();
      updateHeading();
    } else runSearch();
  };

  const initializeAllDirectories = () =>
    document.querySelectorAll(DIRECTORY_SELECTOR).forEach(initializeDirectory);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAllDirectories);
  else initializeAllDirectories();
  document.addEventListener('shopify:section:load', initializeAllDirectories);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(DIRECTORY_SELECTOR).forEach((section) => section._swDirectoryController?.abort());
  });
})();
